import { css, cx } from "styled-system/css";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { DashboardEmpty } from "@/components/dashboard/dashboard-empty";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useConfirm } from "@/hooks/useConfirm";
import { useToast } from "@/hooks/useToast";

type WaitlistStatus = "waiting" | "notified" | "booked" | "cancelled";

type WaitlistRow = {
  id: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  serviceId: string | null;
  serviceName: string | null;
  preferredDateFrom: string | null;
  preferredDateTo: string | null;
  status: WaitlistStatus;
  notifiedAt: string | null;
  createdAt: string;
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function preferredRangeLabel(row: WaitlistRow): string {
  if (!row.preferredDateFrom && !row.preferredDateTo) return "Cualquier fecha";
  if (row.preferredDateFrom && row.preferredDateTo) {
    return `${formatDate(row.preferredDateFrom)} – ${formatDate(row.preferredDateTo)}`;
  }
  if (row.preferredDateFrom) return `Desde ${formatDate(row.preferredDateFrom)}`;
  return `Hasta ${formatDate(row.preferredDateTo!)}`;
}

function statusBadge(status: WaitlistStatus): { label: string; bg: string; color: string } {
  switch (status) {
    case "notified":
      return { label: "Notificado", bg: "#fef9c3", color: "#a16207" };
    case "booked":
      return { label: "Reservó", bg: "#dcfce7", color: "#15803d" };
    case "cancelled":
      return { label: "Cancelado", bg: "#f1f5f9", color: "#64748b" };
    default:
      return { label: "En espera", bg: "#dbeafe", color: "#1d4ed8" };
  }
}

function WaitlistRowItem({ row, onCancel }: { row: WaitlistRow; onCancel: () => void }) {
  const badge = statusBadge(row.status);
  const cancellable = row.status === "waiting" || row.status === "notified";

  return (
    <li
      className={cx(
        cf.dashSurface,
        css({
          p: "4",
          display: "flex",
          flexDir: { base: "column", sm: "row" },
          justifyContent: "space-between",
          gap: { base: "3", sm: "3" },
          opacity: row.status === "cancelled" ? 0.6 : 1,
        }),
      )}
    >
      <div className={css({ minW: 0 })}>
        <div className={css({ display: "flex", alignItems: "center", gap: "2", flexWrap: "wrap" })}>
          <p className={css({ fontWeight: "semibold", color: "#0f172a" })}>{row.clientName}</p>
          <span
            className={css({
              fontSize: "11px",
              fontWeight: "600",
              px: "2.5",
              py: "0.5",
              rounded: "full",
              whiteSpace: "nowrap",
            })}
            style={{ background: badge.bg, color: badge.color }}
          >
            {badge.label}
          </span>
        </div>
        <p className={css({ fontSize: "sm", color: "#64748b", mt: "1" })}>
          {row.clientEmail} · {row.clientPhone}
        </p>
        <p className={css({ fontSize: "sm", color: "#334155", mt: "1" })}>
          {row.serviceName ?? "Cualquier consulta"} · {preferredRangeLabel(row)}
        </p>
        <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "1.5" })}>
          Anotado el {formatDate(row.createdAt)}
          {row.notifiedAt ? ` · Notificado el ${formatDate(row.notifiedAt)}` : ""}
        </p>
      </div>
      {cancellable ? (
        <div className={css({ display: "flex", alignItems: { base: "stretch", sm: "center" }, flexShrink: 0 })}>
          <button
            type="button"
            onClick={onCancel}
            className={css({
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              h: { base: "11", sm: "auto" },
              fontSize: "xs",
              px: "3",
              py: "1.5",
              rounded: "md",
              borderWidth: "1px",
              borderColor: "#fca5a5",
              color: "#dc2626",
              bg: "white",
              cursor: "pointer",
              whiteSpace: "nowrap",
              _hover: { bg: "#fef2f2" },
            })}
          >
            Cancelar
          </button>
        </div>
      ) : null}
    </li>
  );
}

export function DashboardWaitlist() {
  const { refresh } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const [rows, setRows] = useState<WaitlistRow[] | null>(null);
  const [waitlistEnabled, setWaitlistEnabled] = useState<boolean | null>(null);

  const reload = useCallback(async () => {
    const [rList, rMe] = await Promise.all([
      fetchWithSessionSync("/api/dashboard/waitlist", { credentials: "include" }, refresh),
      fetchWithSessionSync("/api/dashboard/me", { credentials: "include" }, refresh),
    ]);
    if (rList.ok) setRows((await rList.json()) as WaitlistRow[]);
    if (rMe.ok) {
      const me = (await rMe.json()) as { professional?: { waitlistEnabled?: boolean } };
      setWaitlistEnabled(me.professional?.waitlistEnabled ?? false);
    }
  }, [refresh]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function cancelEntry(row: WaitlistRow) {
    if (
      !(await confirm({
        message: `¿Cancelar la anotación de "${row.clientName}" en la lista de espera?`,
        tone: "danger",
        confirmLabel: "Cancelar",
      }))
    )
      return;
    const r = await fetchWithSessionSync(
      `/api/dashboard/waitlist/${row.id}`,
      { method: "DELETE", credentials: "include" },
      refresh,
    );
    if (!r.ok) {
      toast.error("No se pudo cancelar la anotación.");
      return;
    }
    await reload();
  }

  if (!rows) {
    return (
      <DashboardPageShell eyebrow="Reservas" title="Lista de espera">
        <LoadingFrame variant="light" minHeight={280} />
      </DashboardPageShell>
    );
  }

  return (
    <DashboardPageShell
      eyebrow="Reservas"
      title="Lista de espera"
      description="Pacientes que se anotaron para que les avisemos si se libera un turno compatible."
    >
      {waitlistEnabled === false ? (
        <div
          className={css({
            mb: "5",
            px: "4",
            py: "3",
            rounded: "lg",
            bg: "#fef9c3",
            borderWidth: "1px",
            borderColor: "#fde68a",
            fontSize: "sm",
            color: "#854d0e",
          })}
        >
          La lista de espera está desactivada.{" "}
          <Link to="/dashboard/settings/behavior" className={css({ textDecoration: "underline", fontWeight: "semibold" })}>
            Activala en Configuración → Comportamiento
          </Link>
          .
        </div>
      ) : null}

      {rows.length === 0 ? (
        <DashboardEmpty
          title="Todavía no hay pacientes en lista de espera"
          hint="Cuando alguien se anote desde tu página de reservas, va a aparecer acá."
        />
      ) : (
        <ul className={css({ display: "flex", flexDir: "column", gap: "3" })}>
          {rows.map((row) => (
            <WaitlistRowItem key={row.id} row={row} onCancel={() => void cancelEntry(row)} />
          ))}
        </ul>
      )}
    </DashboardPageShell>
  );
}
